import { useRef, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BankCashAccountSchema,
  ClientSchema,
  CustomerInvoiceSchema,
  CustomerReceiptDetailSchema,
  CustomerReceiptSchema,
  type ClientDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { getData, getPage, newIdempotencyKey, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { useUrlList } from '../../shared/ui/url-list';
import { ActiveClientPicker } from '../crm/common';
import { refreshAfterPosting } from '../finance/cache';
import { Facts, JournalRef, RecordFailure, RecordLink, useDimensionNames } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { MasterSelect, useActiveMasters } from '../finance/lookups';
import { isAmount, sum, toFils } from '../finance/money';
import { RefusalState } from '../finance/refusal';
import { CUSTOMER_INVOICES } from './CustomerInvoicesPages';
import { SearchFilterBar } from '../../shared/ui/filters';

const RECEIPTS = '/api/v1/finance/customer-receipts';
const RECEIPT = 'finance.customer_receipt';

/** The client's issued invoices with something outstanding, oldest first. */
function useOpenInvoices(clientId: string) {
  return useQuery({
    queryKey: ['customer-invoices', 'open', clientId],
    enabled: clientId !== '',
    queryFn: () =>
      getPage(
        `${CUSTOMER_INVOICES}?pageSize=100&sort=invoice_date&filter[client_id]=${clientId}` +
          '&filter[status]=issued&filter[open]=true',
        CustomerInvoiceSchema,
      ),
  });
}

/** Customer receipts (FRD-FIN-061, FIN-063): with what each allocated and any advance held. */
export function CustomerReceiptsPage() {
  const session = useSession();
  const list = useUrlList({ pageSize: 50 });
  const params = list.apiParams(
    { status: 'status', client: 'client_id', advance: 'with_advance' },
    '-receipt_date',
  );
  const receipts = useQuery({
    queryKey: ['customer-receipts', params.toString()],
    queryFn: () => getPage(`${RECEIPTS}?${params.toString()}`, CustomerReceiptSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Customer receipts"
        description="Money received from clients, allocated to their invoices or held as an advance."
        actions={
          hasCapability(session.data, `${RECEIPT}:create`) ? (
            <Button
              component={RouterLink}
              to="/customer-receipts/new"
              variant="contained"
              startIcon={<AddIcon />}
            >
              New receipt
            </Button>
          ) : undefined
        }
      />
      <Box role="search" aria-label="Filter the receipts">
        <SearchFilterBar>
          <TextField
            select
            size="small"
            label="Status"
            value={list.get('status')}
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              list.set('status', e.target.value);
            }}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="posted">Posted</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Advance"
            value={list.get('advance')}
            sx={{ minWidth: 200 }}
            onChange={(e) => {
              list.set('advance', e.target.value);
            }}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="true">With an advance held</MenuItem>
          </TextField>
        </SearchFilterBar>
      </Box>
      <Section>
        {receipts.isPending && <LoadingState label="Loading receipts" />}
        {receipts.isError && <ErrorState message={receipts.error.message} />}
        {receipts.data?.items.length === 0 && <EmptyState title="No receipts found" />}
        {receipts.data !== undefined && receipts.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Customer receipts">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell align="right">Advance</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.data.items.map((receipt) => (
                  <TableRow key={receipt.id} hover>
                    <TableCell>
                      <RecordLink to={`/customer-receipts/${receipt.id}`}>
                        {receipt.documentNo}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{receipt.clientLabel ?? '—'}</TableCell>
                    <TableCell>{receipt.receiptDate}</TableCell>
                    <TableCell align="right">{receipt.amount}</TableCell>
                    <TableCell align="right">{receipt.allocatedAmount}</TableCell>
                    <TableCell align="right">{receipt.unallocatedAmount}</TableCell>
                    <TableCell>
                      <StatusChip status={receipt.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={receipts.data.total}
              page={list.page}
              rowsPerPage={list.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => {
                list.setPage(next);
              }}
              onRowsPerPageChange={(e) => {
                list.setPageSize(Number.parseInt(e.target.value, 10));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

/**
 * Records a receipt (`/customer-receipts/new`; FRD-FIN-061): the client, date, amount and the
 * register the money reached, and its allocation across the client's open invoices. Whatever is
 * not allocated is held as a customer advance when it is posted (FRD-FIN-063).
 */
export function NewCustomerReceiptPage() {
  const [params] = useSearchParams();
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = businessToday(session.data?.businessTimezone);
  const presetClient = params.get('client') ?? '';
  const presetInvoice = params.get('invoice') ?? '';
  const preset = useQuery({
    queryKey: ['crm', 'client', presetClient],
    enabled: presetClient !== '' && hasCapability(session.data, 'crm.client:view'),
    queryFn: () => getData(`/api/v1/crm/clients/${presetClient}`, ClientSchema),
  });
  const [picked, setPicked] = useState<ClientDto | null>(null);
  const client = picked ?? preset.data ?? null;
  const [receiptDate, setReceiptDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [registerId, setRegisterId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [reference, setReference] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const key = useRef(newIdempotencyKey());
  const registers = useQuery({
    queryKey: ['bank-cash', 'registers', 'active'],
    queryFn: () =>
      getPage(
        '/api/v1/finance/bank-cash-accounts?pageSize=100&filter[status]=active',
        BankCashAccountSchema,
      ),
  });
  const canSeeCostCentres = hasCapability(session.data, 'organization.cost_center:view');
  const costCentres = useActiveMasters('/api/v1/organization/cost-centers', canSeeCostCentres);
  const open = useOpenInvoices(client?.id ?? '');
  if (presetInvoice !== '' && open.data !== undefined && allocations[presetInvoice] === undefined) {
    const target = open.data.items.find((i) => i.id === presetInvoice);
    if (target !== undefined) setAllocations({ [presetInvoice]: target.outstandingAmount });
  }
  const entries = Object.entries(allocations).filter(([, value]) => value.trim() !== '');
  const allocated = sum(entries.map(([, value]) => value));
  const create = useMutation({
    mutationFn: () =>
      postJson(
        RECEIPTS,
        {
          clientId: client?.id ?? '',
          receiptDate,
          amount: amount.trim(),
          bankCashAccountId: registerId,
          ...(costCenterId !== '' ? { costCenterId } : {}),
          ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
          allocations: entries.map(([customerInvoiceId, allocatedAmount]) => ({
            customerInvoiceId,
            allocatedAmount: allocatedAmount.trim(),
            allocationDate: receiptDate,
          })),
        },
        CustomerReceiptDetailSchema,
        session.data?.csrfToken ?? '',
        { idempotencyKey: key.current },
      ),
    onSuccess: async (saved) => {
      await refreshAfterPosting(queryClient);
      await navigate(`/customer-receipts/${saved.receipt.id}`);
    },
  });
  const problems = [
    ...(client === null ? ['Choose the client.'] : []),
    ...(!isAmount(amount) || toFils(amount) <= 0n ? ['Enter the amount received.'] : []),
    ...(registerId === '' ? ['Choose the register the money reached.'] : []),
    ...entries
      .filter(([, v]) => !isAmount(v))
      .map(([id]) => `The allocation to invoice #${id} is not an amount.`),
    ...(isAmount(amount) && toFils(allocated) > toFils(amount)
      ? ['The allocations exceed the amount received.']
      : []),
  ];
  const back = (
    <Button component={RouterLink} to="/customer-receipts" startIcon={<ArrowBackIcon />}>
      All receipts
    </Button>
  );
  return (
    <Stack spacing={3}>
      <PageHeader
        title="New customer receipt"
        description="Saved as a draft; posting moves the money and the receivable."
        actions={back}
      />
      <Section title="Receipt">
        <Stack spacing={2} sx={{ p: 2 }}>
          <Box sx={{ maxWidth: 480 }}>
            <ActiveClientPicker
              label="Client"
              required
              value={client}
              onChange={(value) => {
                setPicked(value);
                setAllocations({});
              }}
            />
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              type="date"
              label="Receipt date"
              value={receiptDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setReceiptDate(e.target.value);
              }}
            />
            <TextField
              label="Amount (AED)"
              required
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
              }}
            />
            <TextField
              select
              label="Received into"
              required
              value={registerId}
              sx={{ minWidth: 240 }}
              onChange={(e) => {
                setRegisterId(e.target.value);
              }}
            >
              {(registers.data?.items ?? []).map((register) => (
                <MenuItem key={register.id} value={register.id}>
                  {register.code} — {register.name}
                </MenuItem>
              ))}
            </TextField>
            <MasterSelect
              label="Cost centre"
              value={costCenterId}
              disabled={!canSeeCostCentres}
              helperText="Where the receivable or advance account requires one"
              options={costCentres.data?.items ?? []}
              onChange={setCostCenterId}
            />
          </Stack>
          <TextField
            label="Reference"
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
            }}
          />
        </Stack>
      </Section>
      <Section title="Allocation to open invoices">
        {client === null ? (
          <EmptyState title="Choose the client to see its open invoices" />
        ) : open.data?.items.length === 0 ? (
          <EmptyState
            title="No open invoices"
            detail="The whole receipt will be held as an advance."
          />
        ) : (
          <Table size="small" aria-label="Open invoices">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="right">Allocate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(open.data?.items ?? []).map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.documentNo}</TableCell>
                  <TableCell>{invoice.dueDate ?? '—'}</TableCell>
                  <TableCell align="right">{invoice.outstandingAmount}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={allocations[invoice.id] ?? ''}
                      slotProps={{
                        htmlInput: {
                          'aria-label': `Allocate to ${invoice.documentNo ?? invoice.id}`,
                        },
                      }}
                      onChange={(e) => {
                        setAllocations((current) => ({ ...current, [invoice.id]: e.target.value }));
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Typography variant="body2" sx={{ p: 2 }}>
          Allocated {allocated}
          {isAmount(amount) && toFils(amount) > toFils(allocated)
            ? ` · held as advance ${sum([amount, `-${allocated}`])}`
            : ''}
        </Typography>
      </Section>
      {problems.length > 0 && (
        <Alert severity="info" variant="outlined">
          <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      {create.isError && <RefusalState error={create.error} />}
      <Box>
        <Button
          variant="contained"
          disabled={problems.length > 0 || create.isPending}
          onClick={() => {
            create.mutate();
          }}
        >
          Save draft
        </Button>
      </Box>
    </Stack>
  );
}

/**
 * One receipt (`/customer-receipts/:id`): Post on a draft; on a posted receipt with an advance
 * held, Apply advance against another open invoice of the client (FRD-FIN-063).
 */
export function CustomerReceiptDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const names = useDimensionNames();
  const today = businessToday(session.data?.businessTimezone);
  const [dialog, setDialog] = useState<'post' | 'apply' | null>(null);
  const [target, setTarget] = useState('');
  const [applyAmount, setApplyAmount] = useState('');
  const detail = useQuery({
    queryKey: ['customer-receipt', id],
    queryFn: () => getData(`${RECEIPTS}/${id}`, CustomerReceiptDetailSchema),
  });
  const open = useOpenInvoices(dialog === 'apply' ? (detail.data?.receipt.clientId ?? '') : '');
  const done = async () => {
    setDialog(null);
    await refreshAfterPosting(queryClient);
    await queryClient.invalidateQueries({ queryKey: ['customer-receipt', id] });
  };
  const post = useMutation({
    mutationFn: (version: number) =>
      postJson(
        `${RECEIPTS}/${id}/post`,
        {},
        CustomerReceiptDetailSchema,
        session.data?.csrfToken ?? '',
        {
          expectedVersion: version,
        },
      ),
    onSuccess: done,
  });
  const apply = useMutation({
    mutationFn: () =>
      postJson(
        `${RECEIPTS}/${id}/apply-advance`,
        { customerInvoiceId: target, allocatedAmount: applyAmount.trim(), allocationDate: today },
        CustomerReceiptDetailSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: done,
  });
  const back = (
    <Button component={RouterLink} to="/customer-receipts" startIcon={<ArrowBackIcon />}>
      All receipts
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the receipt" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Customer receipt" noun="receipt" error={detail.error} back={back} />
    );
  }
  const { receipt, register, journal, allocations } = detail.data;
  const canPost = hasCapability(session.data, `${RECEIPT}:post`);
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Receipt ${receipt.documentNo}`}
        description={receipt.clientLabel ?? 'Customer receipt'}
        actions={
          <>
            {back}
            {receipt.status === 'draft' && canPost && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('post');
                }}
              >
                Post
              </Button>
            )}
            {receipt.status === 'posted' && receipt.unallocatedAmount !== '0.00' && canPost && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('apply');
                }}
              >
                Apply advance
              </Button>
            )}
          </>
        }
      />
      {post.isError && <RefusalState error={post.error} />}
      <Section title="Receipt">
        <Facts
          label="Receipt details"
          facts={
            [
              ['Status', <StatusChip key="s" status={receipt.status} />],
              [
                'Client',
                <RecordLink key="c" to={`/crm/clients/${receipt.clientId}`}>
                  {receipt.clientLabel ?? receipt.clientId}
                </RecordLink>,
              ],
              ['Date', receipt.receiptDate],
              ['Amount', receipt.amount],
              ['Allocated', receipt.allocatedAmount],
              ['Held as customer advance', receipt.unallocatedAmount],
              [
                'Received into',
                register === null ? (
                  '—'
                ) : (
                  <RecordLink key="r" to={`/bank-cash/movements/${receipt.bankCashTransactionId}`}>
                    {register.code} — {register.name}
                  </RecordLink>
                ),
              ],
              ['Cost centre', names.costCentre(receipt.costCenterId)],
              ['Reference', receipt.reference ?? '—'],
              ['Journal', <JournalRef key="j" journal={journal} />],
            ] as [string, ReactNode][]
          }
        />
      </Section>
      <Section title="Allocations">
        {allocations.length === 0 ? (
          <EmptyState title="Nothing allocated" />
        ) : (
          <Table size="small" aria-label="Allocations">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>From advance</TableCell>
                <TableCell>Journal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allocations.map((allocation) => (
                <TableRow key={allocation.id}>
                  <TableCell>
                    <RecordLink to={`/customer-invoices/${allocation.customerInvoiceId}`}>
                      {allocation.invoiceDocumentNo ?? allocation.customerInvoiceId}
                    </RecordLink>
                  </TableCell>
                  <TableCell>{allocation.allocationDate}</TableCell>
                  <TableCell align="right">{allocation.allocatedAmount}</TableCell>
                  <TableCell>{allocation.fromAdvance ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    {allocation.journalId === null ? (
                      '—'
                    ) : (
                      <RecordLink to={`/journals/${allocation.journalId}`}>Journal</RecordLink>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      <DocumentsPanel
        recordType={RECEIPT}
        recordId={receipt.id}
        canAttach={hasCapability(session.data, `${RECEIPT}:create`)}
      />
      <RecordHistory recordType={RECEIPT} recordId={receipt.id} />
      {dialog === 'post' && (
        <ConfirmDialog
          open
          title={`Post ${receipt.documentNo}?`}
          message="Posting records the money in the register, settles the allocated invoices and holds any remainder as a customer advance."
          confirmLabel="Post"
          busy={post.isPending}
          error={post.isError ? post.error.message : null}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            post.mutate(receipt.version);
          }}
        />
      )}
      {dialog === 'apply' && (
        <FormDialog
          open
          title="Apply the advance to an invoice"
          submitLabel="Apply"
          canSubmit={target !== '' && isAmount(applyAmount) && toFils(applyAmount) > 0n}
          submitting={apply.isPending}
          error={apply.isError ? apply.error.message : null}
          onClose={() => {
            setDialog(null);
          }}
          onSubmit={() => {
            apply.mutate();
          }}
        >
          <Typography variant="body2">Advance held: {receipt.unallocatedAmount}</Typography>
          <TextField
            select
            label="Invoice"
            required
            value={target}
            onChange={(e) => {
              setTarget(e.target.value);
              const invoice = open.data?.items.find((i) => i.id === e.target.value);
              if (invoice !== undefined) {
                setApplyAmount(
                  toFils(invoice.outstandingAmount) < toFils(receipt.unallocatedAmount)
                    ? invoice.outstandingAmount
                    : receipt.unallocatedAmount,
                );
              }
            }}
          >
            {(open.data?.items ?? []).map((invoice) => (
              <MenuItem key={invoice.id} value={invoice.id}>
                {invoice.documentNo} — outstanding {invoice.outstandingAmount}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount"
            required
            value={applyAmount}
            onChange={(e) => {
              setApplyAmount(e.target.value);
            }}
          />
        </FormDialog>
      )}
    </Stack>
  );
}
